import { Component, ElementRef, EventEmitter, Output, ViewChild, ChangeDetectorRef, Input, OnChanges, OnDestroy, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription, catchError, of, switchMap, takeUntil, tap, timer } from 'rxjs';
import { LocationLookupService, locationError } from 'src/app/services/location-lookup.service';

@Component({
  selector: 'app-location-search',
  templateUrl: './location-search.component.html',
  styleUrls: ['./location-search.component.scss']
})
export class LocationSearchComponent implements OnChanges, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('suggestionsList') suggestionsList?: ElementRef<HTMLElement>;
  @Output() locationSelected = new EventEmitter<{lat: number, lng: number, address: string} | null>();
  @Output() locationFocusChange = new EventEmitter<boolean>();
  @Output() locationHoveredChanged = new EventEmitter<boolean>();
  @Output() locationInputChanged = new EventEmitter<string>();

  searchControl = new FormControl('');
  suggestions: any[] = [];
  isclearButtonShow: boolean = false;
  @Input() isClear: boolean = false;
  @Input() selectedAddress: string | null = null;
  private lastAppliedAddress: string | null = null;
  private isInputFocused: boolean = false;
  isDropdownOpenUp: boolean = false;
  searchError = '';
  private readonly destroyed$ = new Subject<void>();
  private readonly searchCancelled$ = new Subject<void>();
  private coordinateRequest?: Subscription;
  private destroyed = false;

  constructor(
    private lookup: LocationLookupService,
    private cdr: ChangeDetectorRef
  ) {
    // Setup search with debounce
    this.searchControl.valueChanges.pipe(
      tap((searchText: string) => this.locationInputChanged.emit(searchText || '')),
      switchMap((searchText: string) => {
        this.coordinateRequest?.unsubscribe();
        this.suggestions = [];
        this.searchError = '';
        const query = (searchText || '').trim();
        if (!query) {
          this.clearLocation();
          this.isclearButtonShow = false;
          return of([]);
        }
        this.isclearButtonShow = true;
        if (query.length < 3) return of([]);
        return timer(300).pipe(
          switchMap(() => this.lookup.search(query)),
          catchError(error => { this.searchError = locationError(error); return of([]); }),
          takeUntil(this.searchCancelled$)
        );
      }),
      takeUntil(this.destroyed$)
    ).subscribe((response: any) => {
      this.suggestions = response;
      this.cdr.detectChanges(); // Force change detection
      this.scheduleDropdownDirectionUpdate();
    });
  }

  onLocationSelect(suggestion: any) {
    this.searchCancelled$.next();
    this.coordinateRequest?.unsubscribe();
    this.searchError = '';
    this.searchInput.nativeElement.value = suggestion.description;
    this.searchControl.setValue(suggestion.description, { emitEvent: false });
    this.suggestions = [];
    
    if (suggestion.place_id) {
      this.getCoordinates(suggestion.place_id, suggestion.description);
    }
  }

  private getCoordinates(placeId: string, address: string) {
    this.coordinateRequest = this.lookup.coordinates({ place_id: placeId, description: address })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: any) => {
          this.locationSelected.emit({ lat: response.latitude, lng: response.longitude, address: response.address });
        },
        error: (error) => {
          this.searchError = locationError(error);
          this.cdr.detectChanges();
        }
      });
  }

  private clearLocation() {
    this.locationSelected.emit(null);
    this.suggestions = [];
    this.isDropdownOpenUp = false;
  }

  clearSearchbar() {
    this.searchControl.reset();
  }

  focusInput(selectAll: boolean = false) {
    if (this.searchInput?.nativeElement) {
      const input = this.searchInput.nativeElement;
      try {
        input.focus({ preventScroll: true });
      } catch {
        input.focus();
      }
      const valueLength = input.value?.length ?? 0;
      try {
        if (selectAll && valueLength > 0) {
          input.select();
        } else {
          input.setSelectionRange(valueLength, valueLength);
        }
      } catch {
        // Some mobile browsers do not support selection on every input state.
      }
    }
  }

  onInputTouch(event: TouchEvent) {
    event.stopPropagation();
    this.focusInput();
  }

  onInputFocus() {
    this.isInputFocused = true;
    this.locationFocusChange.emit(true);
    this.scheduleDropdownDirectionUpdate();
  }

  onInputBlur() {
    this.isInputFocused = false;
    setTimeout(() => {
      if (!this.destroyed) this.locationFocusChange.emit(false);
    }, 200);
  }

  onInputMouseLeave() {
    this.locationHoveredChanged.emit(false);
  }

  ngOnChanges() {
    if (this.isClear) {
      this.suggestions = [];
      this.searchControl.reset();
      this.isclearButtonShow = false;
      this.lastAppliedAddress = null;
      this.isDropdownOpenUp = false;
      return;
    }

    const incomingAddress = (this.selectedAddress || '').trim();
    const currentValue = (this.searchControl.value || '').toString().trim();
    if (!incomingAddress) {
      if (!this.isInputFocused && (this.lastAppliedAddress || currentValue)) {
        this.searchCancelled$.next();
        this.coordinateRequest?.unsubscribe();
        this.searchControl.reset('', { emitEvent: false });
        this.searchError = '';
        this.isclearButtonShow = false;
        this.suggestions = [];
      }
      this.lastAppliedAddress = null;
      return;
    }

    if (!this.isInputFocused) {
      if (incomingAddress !== currentValue) {
        this.searchCancelled$.next();
        this.coordinateRequest?.unsubscribe();
        this.searchControl.setValue(incomingAddress, { emitEvent: false });
        this.searchError = '';
        this.suggestions = [];
        this.isclearButtonShow = true;
        this.isDropdownOpenUp = false;
      }
      this.lastAppliedAddress = incomingAddress;
    }
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onWindowChanged() {
    if (this.suggestions.length > 0) {
      this.scheduleDropdownDirectionUpdate();
    }
  }

  private scheduleDropdownDirectionUpdate() {
    setTimeout(() => { if (!this.destroyed) this.updateDropdownDirection(); }, 0);
  }

  private updateDropdownDirection() {
    if (!this.searchInput?.nativeElement || this.suggestions.length === 0) {
      this.isDropdownOpenUp = false;
      return;
    }

    const inputRect = this.searchInput.nativeElement.getBoundingClientRect();
    const listHeight = this.suggestionsList?.nativeElement?.offsetHeight ?? 200;
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    this.isDropdownOpenUp = spaceBelow < listHeight && spaceAbove > spaceBelow;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroyed$.next(); this.destroyed$.complete();
    this.searchCancelled$.complete();
  }
}
